import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MAIN_URL } from '../../Configs/Urls';
import axios from '../../Configs/axiosInstance';
import {
  Alert, Chip, Paper, Snackbar, Stack, Typography, Box, Avatar, Button,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Divider,
} from '@mui/material';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import CustomizedDataGrid from '../Components/CustomizedDataGrid';
import PaymentFormPage from '../PaymentsPage/Payment-Form';
import { colorForName, initialsFor } from './Components/avatarUtils';

// yyyy-mm-dd (from the backend) -> dd-mm-yyyy, for display.
function formatDate(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function PaymentView() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(true);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [showForm, setShowForm] = useState(false);
  const [paymentId, setPaymentId] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const fetchPatient = () => {
    setLoadingPatient(true);
    axios
      .get(`${MAIN_URL}patients/${patientId}`)
      .then((response) => setPatient(response.data))
      .catch(() => {
        setSnackbarMessage("Error fetching patient data.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      })
      .finally(() => setLoadingPatient(false));
  };

  const fetchPayments = () => {
    setLoadingPayments(true);
    axios
      .get(`${MAIN_URL}payments/patient/${patientId}`)
      .then((response) => setPayments(response.data || []))
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false));
  };

  useEffect(() => {
    if (!patientId) return;
    fetchPatient();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const handleAdd = () => {
    setPaymentId(null);
    setShowForm(true);
  };

  const handleEdit = (row) => {
    setPaymentId(row.id);
    setShowForm(true);
  };

  const handleDelete = (row) => {
    setPaymentId(row.id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${MAIN_URL}payments/${paymentId}`);
      setSnackbarMessage("Payment Deleted Successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchPayments();
    } catch (error) {
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error deleting payment. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setPaymentId(null);
      setDeleteLoading(false);
    }
  };

  const onFormClose = () => {
    setShowForm(false);
    setPaymentId(null);
    fetchPayments();
  };

  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const columns = [
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", fontWeight: 600 }}>
          ₹{params.value}
        </Box>
      ),
    },
    {
      field: "paymentDate",
      headerName: "Payment Date",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {formatDate(params.value)}
        </Box>
      ),
    },
    {
      field: "paymentType",
      headerName: "Payment Type",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={params.value}
            size="small"
            color={params.value === "Cash" ? "success" : params.value === "Online" ? "info" : "default"}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: "purpose",
      headerName: "Purpose",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loadingPatient) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1100 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/patients')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Back to Patients List
      </Button>

      <Stack spacing={3}>
        {/* Patient summary + total paid */}
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, fontSize: '1.2rem', bgcolor: colorForName(patient?.name) }}>
              {initialsFor(patient?.name)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {patient?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patient?.contactNumber}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="caption" color="text.secondary">Total Paid</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                ₹{totalPaid.toLocaleString()}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {showForm ? (
          <Box>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={onFormClose}
              sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Back to Payment History
            </Button>
            <PaymentFormPage
              patientId={patientId}
              paymentId={paymentId}
              name={patient?.name}
              onCloseForm={onFormClose}
            />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Payment History
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleAdd}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Add Payment
              </Button>
            </Box>

            {payments.length === 0 && !loadingPayments ? (
              <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, borderColor: 'divider', textAlign: 'center' }}>
                <PaidRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No payments recorded for this patient yet.</Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: 'divider', overflow: 'hidden' }}>
                <CustomizedDataGrid rows={payments} columns={columns} loading={loadingPayments} />
              </Paper>
            )}
          </>
        )}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setPaymentId(null); }}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this payment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDeleteDialog(false); setPaymentId(null); }} color="primary">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleteLoading}
            variant="contained"
            color="error"
            size="small"
          >
            {deleteLoading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentView;
